<template>
  <div
    v-if="isLoadingInitialData"
    id="dashboard-loader"
    class="flex flex-col items-center justify-center"
  >
    <Spinner />
    <h2 class="font-heading text-2xl">Loading today's performances...</h2>
  </div>
  <div
    v-else
    id="reservations-stats"
    class="flex flex-col justify-start items-start py-10 xl:flex-row"
  >
    <div class="flex flex-col h-full w-full">
      <!--STATS LOADER-->
      <div
        v-if="isFetchingPerformanceReservation"
        id="stats-loader"
        class="flex flex-col items-center justify-start h-96 mb-20"
      >
        <Spinner class="h-auto" />
        <h2 class="font-heading text-2xl">Loading performance stats...</h2>
      </div>
      <!--CURRENT PERFORMANCE STATS-->
      <div v-else-if="selectedPerformance" class="flex flex-col mb-20 lg:flex-row">
        <div class="lg:w-96 lg:mr-20 lg:pr-10">
          <span class="font-heading font-semibold text-xl block">Today</span>
          <div class="relative inline-block mb-3 mt-1">
            <!--NUMBER OF GUESTS-->
            <span
              id="number-of-guests"
              class="font-heading font-semibold text-[150px] lg:text-[230px] block text-fg-secondary leading-[.83]"
            >
              {{ numberOfGuests }}
            </span>
            <!--PROGRESS BAR-->
            <div
              id="sold-percentage-bar"
              class="absolute h-full w-1 border border-linear-subtle -right-6 top-1.5"
            >
              <div
                class="absolute w-full left-0 bottom-0 bg-gray-30"
                :style="`height: ${soldOutPercentage}%`"
              ></div>
            </div>
            <!--PERCENTAGE TEXT-->
            <div id="sold-percentage-text" class="absolute right-0 translate-x-20 top-0.5">
              <span class="font-heading font-semibold text-xl block text-fg-secondary"
                >{{ soldOutPercentage }}%</span
              >
              <span class="font-label text-fg-secondary uppercase text-xs block">SOLD</span>
            </div>
          </div>
          <span class="font-label text-fg-secondary uppercase text-xs block">Guests</span>

          <!--KEY STATS-->
          <div class="mt-8 flex space-x-10">
            <div class="">
              <span
                id="number-of-reservations"
                class="font-heading font-semibold text-6xl block text-fg-secondary"
                >{{ numberOfReservations }}</span
              >
              <span class="font-label text-fg-secondary uppercase text-xs block w-16 mt-1"
                >RESERVATIONS</span
              >
            </div>
            <div class="">
              <span
                id="available-seats"
                class="font-heading font-semibold text-6xl block text-fg-secondary"
                >{{ availableSeats }}</span
              >
              <span class="font-label text-fg-secondary uppercase text-xs block w-16 mt-1"
                >AVAILABLE SEATS</span
              >
            </div>
            <div class="">
              <span
                id="number-of-addons"
                class="font-heading font-semibold text-6xl block text-fg-secondary"
                >{{ numberOfAddOns }}</span
              >
              <span class="font-label text-fg-secondary uppercase text-xs block w-16 mt-1"
                >ADD-ONS</span
              >
            </div>
          </div>

          <!--RESERVATION BUTTON-->
          <ButtonDefault
            id="reservations-button"
            label="Reservations"
            class="mt-8"
            @click="goToPerformanceDetail(selectedPerformance.id)"
          />
        </div>

        <!--TODAY'S PERFORMANCE LIST AND TABLE VIEW-->
        <div id="todays-performances-list" class="mr-20 pt-10 w-full max-w-screen-md pl-4 sm:pl-10">
          <div
            v-if="isAFuturePerformanceSelected"
            class="today-performance mb-4 cursor-pointer text-fg-primary [&>.p-name]:font-bold"
          >
            <span class="font-sans block -mb-1 p-name">{{ selectedPerformance.show.name }}</span>
            <span class="font-sans text-sm">{{ selectedPerformance.time }}</span>
          </div>
          <template v-else>
            <div
              v-for="performance in todayPerformances"
              :key="performance.id"
              :id="`today-performance-${performance.id}`"
              :class="isPerformanceSelectedClasses(performance)"
              class="today-performance mb-4 cursor-pointer"
              @click="setSelectedPerformance(performance)"
            >
              <span class="font-sans block -mb-1 p-name">{{ performance.show.name }}</span>
              <span class="font-sans text-sm">{{ performance.time }}</span>
            </div>
          </template>
          <!--TABLE VIEW-->
          <TableListView
            class="mt-8"
            id="today-performance-table-view"
            :tables="tablesInTableListView"
            :selectedTables="[]"
          />
        </div>
      </div>
      <div v-else id="no-shows-today-message" class="text-xl font-heading font-semibold h-96 mb-20">
        No Shows Today
      </div>
      <!--FOUR NEXT PERFORMANCES-->
      <div class="mt-auto pt-10 border-t border-linear-subtle mr-10">
        <div class="flex items-baseline space-x-6">
          <span class="font-sans font-semibold block mb-4">Coming Up</span>
          <span
            v-if="isAFuturePerformanceSelected"
            id="back-to-today-button"
            class="text-fg-link-primary font-sans cursor-pointer"
            @click="setSelectedPerformance(todayPerformances[0] || null)"
          >
            Today
          </span>
        </div>
        <div
          id="future-performances-list"
          class="flex flex-col space-y-10 md:flex-row md:space-y-0 md:space-x-4 lg:space-x-10"
        >
          <div
            v-for="performance in futurePerformances"
            :key="performance.id"
            :id="`future-performance-${performance.id}`"
            class="future-performance cursor-pointer"
            :class="isPerformanceSelectedClasses(performance)"
            @click="setSelectedPerformance(performance)"
          >
            <span class="font-sans text-base block -mb-1 p-name">{{ performance.show.name }}</span>
            <span class="text-sm">{{ performance.shortDate }} - {{ performance.time }}</span>
          </div>
        </div>
      </div>
    </div>
    <!--RESERVATION LIVE LOGS-->
    <div
      id="reservation-log-list"
      class="pt-10 mt-10 self-stretch h-full overflow-auto grow border-t border-linear-subtle xl:border-l xl:pl-10 xl:w-96 xl:mt-0 xl:pt-0 xl:border-t-0"
    >
      <h2 class="font-heading text-lg mb-4">Reservations</h2>
      <TransitionGroup
        move-class="transition ease-in-out duration-700"
        enter-from-class="opacity-0 -translate-x-8"
        enter-active-class="ease-in-out duration-700"
        enter-to-class="opacity-100 translate-x-0"
        leave-from-class="opacity-100 translate-x-0"
        leave-active-class="ease-in-out duration-700 absolute"
        leave-to-class="opacity-0 -translate-x-8"
      >
        <div
          v-for="reservation in logReservations"
          :key="reservation.id"
          class="reservation-log mb-5"
        >
          <span
            class="text-sm text-fg-secondary block"
            :id="`reservation-${reservation.id}-created-at`"
          >
            {{ reservation.created_time }}
          </span>
          <template v-if="reservation.contact">
            <span
              class="contact-name font-sans cursor-pointer text-fg-link-primary"
              @click="goToGuestDetail(reservation.contact.id)"
            >
              {{ reservation.contact.name }}
            </span>
          </template>
          <template v-else>
            <span class="contact-name font-sans cursor-pointer text-fg-link-primary">A Guest</span>
          </template>
          <span> made a </span>
          <span
            class="reservation-info font-sans cursor-pointer text-fg-link-primary"
            @click="goToReservationPerformancePage(reservation.id, reservation.performance_id)"
          >
            reservation.
          </span>
          <div class="text-sm text-fg-secondary" :id="`reservation-${reservation.id}-show-detail`">
            {{ reservation.showname }} - {{ reservation.performanceLongDate }}
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script lang="ts">
import { useEventBus } from '@vueuse/core'
import moment from 'moment'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import { reservationKey } from '@/api/events'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import Spinner from '@/common/components/atoms/Spinner.vue'
import TableListView from '@/components/TableListView.vue'
import { StorePerformance } from '@/models'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { byId, sortBy, sortByMultiple, sum, todayIsoStr, upsertById } from '@/utils/utils'

import {
  PerformanceReservationsRead,
  ReservationDetailedRead,
  ReservationStatus,
} from '@generated/types'

type SelectedPerformance = PerformanceReservationsRead & {
  shortDate: string
  time: string
  longDate: string
}

type LogReservation = ReservationDetailedRead & {
  created: string
  created_time: string
  performance_id: number
  showname: string
  performanceLongDate: string
}

export default defineComponent({
  name: 'ReservationsDashboard',
  components: { TableListView, ButtonDefault, Spinner },
  data() {
    return {
      currentDate: todayIsoStr(),
      isLoadingInitialData: true,
      selectedPerformance: null as SelectedPerformance | null,
      reservations: [] as ReservationDetailedRead[],
      isFetchingPerformanceReservation: false,
    }
  },
  async mounted() {
    await this.performanceStore.getPerformances({
      start_date: this.currentDate,
      pagination: 'false',
    })

    await this.reservationStore.getReservations()
    this.reservations = this.reservationStore.reservations.slice()
    await this.setSelectedPerformance(this.todayPerformances[0] || null)
    this.isLoadingInitialData = false
    useEventBus(reservationKey).on(this.onReservationChange)
  },
  unmounted() {
    useEventBus(reservationKey).off(this.onReservationChange)
  },
  methods: {
    goToPerformanceDetail(performanceId: number) {
      this.$router.push({
        name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
        params: { performanceId: performanceId.toString() },
      })
    },
    goToGuestDetail(guestId: number) {
      this.$router.push({ name: 'guest', params: { id: guestId.toString() } })
    },
    goToReservationPerformancePage(reservationId: number, performanceId: number) {
      this.$router.push({
        name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
        params: {
          reservationId: reservationId.toString(),
          performanceId: performanceId.toString(),
        },
      })
    },
    async fetchPerformanceReservation(performance: StorePerformance): Promise<SelectedPerformance> {
      this.isFetchingPerformanceReservation = true
      try {
        const performanceReservation = await this.performanceStore.getPerformanceReservations(
          performance.id,
        )
        return {
          ...performanceReservation,
          shortDate: this.settingStore.dateShortStr(performanceReservation.datetime),
          time: this.settingStore.timeStr(performanceReservation.datetime),
          longDate: this.settingStore.dateLongStr(performanceReservation.datetime),
        }
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        this.isFetchingPerformanceReservation = false
      }
    },
    async setSelectedPerformance(performance: StorePerformance | null) {
      this.selectedPerformance = !performance
        ? null
        : await this.fetchPerformanceReservation(performance)
    },
    isPerformanceSelectedClasses(performance: StorePerformance): string {
      return this.selectedPerformance?.id == performance.id
        ? 'text-fg-primary [&>.p-name]:font-bold'
        : 'text-fg-link-primary'
    },
    onReservationChange(reservation: ReservationDetailedRead) {
      upsertById(reservation, this.reservations)
    },
  },
  computed: {
    ...mapStores(usePerformanceStore, useSettingStore, useReservationStore),
    availableSeats(): number {
      if (!this.selectedPerformance) return 0
      return sum(
        this.selectedPerformance.tables
          ?.filter(table => table.available)
          .map(t => t.available_guests),
      )
    },
    logReservations(): LogReservation[] {
      return sortByMultiple(
        this.reservations.map<LogReservation>(reservation => {
          const isCreatedToday = moment(reservation.created).isSame(moment(), 'day')
          const createdTime = isCreatedToday
            ? `Today @ ${this.settingStore.timeStr(reservation.created)}`
            : this.settingStore.dateTimeStr(reservation.created)
          return {
            ...reservation,
            created_time: createdTime,
            showname: reservation.performance.show?.name,
            performanceLongDate: this.settingStore.dateLongStr(reservation.performance.datetime),
          }
        }),
        ['-created', 'id'],
      ).slice(0, 10)
    },
    numberOfReservations(): number {
      if (!this.selectedPerformance) return 0
      return this.selectedPerformance.reservations.filter(
        reservation => reservation.status != ReservationStatus.CANCELED,
      ).length
    },
    numberOfAddOns(): number {
      if (!this.selectedPerformance) return 0
      return sum(
        this.selectedPerformance.reservations.map(reservation =>
          sum(reservation.add_ons.map(ao => ao.quantity)),
        ),
      )
    },
    numberOfGuests(): number {
      if (!this.selectedPerformance) return 0
      return sum(
        this.selectedPerformance.reservations
          .filter(reservation => reservation.status != ReservationStatus.CANCELED)
          .map(reservation => reservation.number_of_guests),
      )
    },
    soldOutPercentage(): number {
      if (!this.selectedPerformance) return 0
      const soldTables = this.selectedPerformance.tables.filter(t => !t.available)
      return Math.round((soldTables.length * 100) / this.selectedPerformance.tables.length)
    },
    tablesInTableListView() {
      if (!this.selectedPerformance) return []
      return this.selectedPerformance.tables.map(table => {
        const reservation = this.selectedPerformance?.reservations
          .filter(
            r => r.status != ReservationStatus.CANCELED && r.status != ReservationStatus.CLEARED,
          )
          .find(r => r.tables.includes(table.id))

        return {
          ...table,
          badge:
            reservation &&
            reservation.status != ReservationStatus.EXPECTED &&
            reservation.status.replace(' ', '-'),
          available: table.available_guests > 0 || !reservation,
        }
      })
    },
    isAFuturePerformanceSelected(): boolean {
      if (this.selectedPerformance)
        return this.futurePerformances.some(byId(this.selectedPerformance.id))
      return false
    },
    todayPerformances(): StorePerformance[] {
      return this.performanceStore.performances.filter(p => p.date === this.currentDate)
    },
    futurePerformances(): (StorePerformance & { shortDate: string })[] {
      return sortBy(
        this.performanceStore.performances.filter(p => p.date > this.currentDate),
        'datetime',
      )
        .slice(0, 4)
        .map(p => ({ ...p, shortDate: this.settingStore.dateShortStr(p.date) }))
    },
  },
})
</script>
